<template>
  <Alert v-if="frozen" variant="error" square class="mb-[3px]">
    {{ t("licensing.page.orgSuspended") }}
    <template v-if="frozenReason">
      {{ t(statusChangeReasonKey(frozenReason)) }}.
    </template>
    <!-- an MSP admin viewing one of its Managed organizations -->
    <template v-if="managedByUs">
      <RouterLink
        v-if="managedOrgsEnabled"
        to="/organization/managed-orgs"
        class="underline hover:no-underline"
        @click="selectPrimary"
        >{{ t("licensing.page.orgSuspendedRestoreHint") }}</RouterLink
      >
      <template v-else>{{ t("licensing.page.orgSuspendedRestoreHint") }}</template>
    </template>
  </Alert>

  <Toast
    v-model:model-value="showToast"
    :message="t('licensing.page.orgSuspended')"
    variant="error"
    position="bottom-right"
    :duration="4000"
  />
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Role } from "tf-api-client";
import { useAuth } from "../../composables/use-auth";
import { useOrgStatus } from "../../composables/use-org-status";
import { statusChangeReasonKey } from "../../config/licensing";
import { sessionEvents } from "../../services/session-events";
import { isFeatureEnabled } from "../../utils/feature-flags";
import Alert from "../Base/Alert.vue";
import Toast from "../Base/Toast.vue";

// The selected organization is suspended while the session is not (plan
// section 4.3.3): say so with the reason on every page, for that organization
// only. A session whose own organization is suspended never gets here (the
// suspended page takes over).

const { t } = useI18n();
const { user, selectedMembership, selectOrganization } = useAuth();

// `/org/:id` is for org admins only. A standard user can't be in a suspended
// organization with an unrestricted token anyway: sign-in is refused.
const orgId = computed(() =>
  selectedMembership.value?.role === Role.OrgAdmin
    ? selectedMembership.value.organizationId
    : null,
);
const { frozen, frozenReason } = useOrgStatus(orgId);

// A non-primary membership is a Managed organization of the user's own
// (Manager) organization: its seats are restored from there.
const managedByUs = computed(() => selectedMembership.value?.primary === false);
const managedOrgsEnabled = isFeatureEnabled("/organization/managed-orgs");

const selectPrimary = () => {
  const primary = user.value?.memberships.find((m) => m.primary);
  if (primary) selectOrganization(primary.organizationId);
};

// An action on the selected organization was refused because it is
// suspended: the screen's own error may be generic, so say why.
const showToast = ref(false);
const stop = sessionEvents.on((event) => {
  if (event.type === "orgSuspendedAction" && event.orgId === orgId.value) {
    showToast.value = true;
  }
});
onUnmounted(stop);
</script>
